import {
  Button,
  Image,
  Listbox,
  ListboxItem,
  Navbar,
  NavbarBrand,
  NavbarContent,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@nextui-org/react';
import { CtyButton } from '../button/CtyButton';
import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router-dom';
import { checkType, CtyShowToast } from '../toast/CtyToast';
import { getPalUrl, injectPalChatbot } from '../utils/commonutils';
import { useScreenWidth } from '../utiluicomponents/useScreenWidth';
import { CartItemsList } from '../utiluicomponents/cartItemsList';
import { RootState, useGetTenantMetadataQuery } from '@circuitry-ai/doc-data';
import { useSelector } from 'react-redux';
import { useTheme } from '../context/ThemeContext';
import { ThemeModeSwitcher } from './ThemeModeSwitcher';
import { useSyncDefaultAdvisorPalMode } from './useSyncDefaultAdvisorPalMode';
import { HeaderAccountControls } from './HeaderAccountControls';
import { useHoverMenu } from '../utiluicomponents/useHoverMenu';
export interface CtyHeaderProps {
  logo?: string;
  onOpen?: () => void;
  isFileViewer?: boolean;
  onLogout?: () => void;
  onSwitchOrganisation?: (name: string) => void;
}

export function CtyHeader(props: Readonly<CtyHeaderProps>) {
  const { onOpen, isFileViewer = false, onLogout, onSwitchOrganisation } = props;
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const width = useScreenWidth();
  const token = useSelector((state: RootState) => state.auth.token);
  const isMobile = width < 640;
  const { data: tenantMetadata } = useGetTenantMetadataQuery(undefined, { skip: !token });
  const defaultAdvisorMode = useSyncDefaultAdvisorPalMode();
  const palMenu = useHoverMenu();

  const handleLaunchPAL = (key: string) => {
    if (key === 'window_view') {
      const palUrl = getPalUrl();
      if (palUrl) {
        window.open(palUrl, '_blank', 'noopener,noreferrer');
      }
    } else if (key === 'in_app_view') {
      injectPalChatbot(CtyShowToast, checkType, tenantMetadata?.widget_icon_url, {
        open: true,
        resolveTargetMode: () => defaultAdvisorMode,
      });
    }
  };
  
  return (
    <Navbar isBordered maxWidth="full" isBlurred={false} position="sticky" classNames={{ base: 'cty-app-header' }}>
      <div className="sm:hidden">
        <CtyButton
          isIconOnly
          className="flex  "
          size="sm"
          variant="light"
          onPress={onOpen}
        >
          <Icon
            className="text-[#181818] dark:text-[var(--cty-text-secondary)]"
            height={24}
            icon="solar:hamburger-menu-outline"
            width={24}
          />
        </CtyButton>
      </div>
      {isMobile && (
        <div>
          <NavbarBrand>
            <Image
              className="cursor-pointer"
              onClick={() => navigate('/')}
              src={props.logo}
              alt="Logo"
              width={150}
            />
          </NavbarBrand>
        </div>
      )}
      {isFileViewer && (
        <CtyButton className='flex gap-4 items-center h-[40px]' variant='light' disableRipple={true} size="sm" onPress={() => navigate('/')}>
          <Image src={isDark ? '/Logo-dark.svg' : '/Logo.svg'} alt="Logo" width={150} />
        </CtyButton>
      )}

      <NavbarContent as="div" justify="end" id="user-profile-btn" className="flex items-center gap-8">
        <Popover placement="bottom-end" isOpen={palMenu.isOpen} onOpenChange={palMenu.setIsOpen} shouldBlockScroll={false}>
          <PopoverTrigger>
            <Button
              id="open-pal-btn"
              variant="light"
              size="sm"
              disableAnimation
              className="hidden h-9 rounded-full px-4 text-sm font-medium text-[#18181b] hover:bg-[#F4F4F5] aria-expanded:!scale-100 aria-expanded:!opacity-100 aria-expanded:!font-medium data-[hover=true]:!font-medium data-[pressed=true]:!scale-100 dark:text-foreground dark:hover:bg-content2 sm:flex"
              endContent={<Icon icon="lucide:chevron-down" width={17} height={17} className="text-[#181818] dark:text-[var(--cty-text-secondary)]" />}
              onMouseEnter={palMenu.open}
              onMouseLeave={palMenu.closeSoon}
            >
              Open PAL
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="cty-header-menu rounded-xl border border-[#E4E4E7] p-1.5 shadow-[0_8px_24px_rgba(0,0,0,0.10)] dark:border-divider"
            onMouseEnter={palMenu.open}
            onMouseLeave={palMenu.closeSoon}
          >
            <Listbox
              aria-label="Open PAL"
              onAction={(key) => {
                handleLaunchPAL(String(key));
                palMenu.close();
              }}
              className="p-0"
              itemClasses={{ base: 'rounded-[8px] data-[hover=true]:bg-[#F4F4F5] dark:data-[hover=true]:bg-content2' }}
            >
              <ListboxItem key="window_view" textValue="New PAL Window" endContent={<Icon icon="radix-icons:open-in-new-window" className="text-[#71717a] dark:text-[var(--cty-text-secondary)]" />}>
                New PAL Window
              </ListboxItem>
              <ListboxItem key="in_app_view" textValue="Widget" endContent={<Icon icon="solar:chat-line-linear" className="text-[#71717a] dark:text-[var(--cty-text-secondary)]" />}>
                Widget
              </ListboxItem>
            </Listbox>
          </PopoverContent>
        </Popover>
        <div className="flex items-center gap-1">
          <ThemeModeSwitcher />
          <CartItemsList />
        </div>
        <div className="h-6 w-px bg-divider" aria-hidden />
        <HeaderAccountControls onLogout={onLogout} onSwitchOrganisation={onSwitchOrganisation} />
      </NavbarContent>
    </Navbar>
  );
}

export default CtyHeader;
